import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { getImportSessions, acceptImportSession, rejectImportSession } from 'src/shared/services/memberImportSessionService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useSignalRContext } from 'src/shared/contexts/signalRProvider';
import { SignalRMessageTypeEnum } from 'src/shared/models/enums/signalRMessageType.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { MemberImportSession } from '../models/memberImportSession';

export const MemberImportSessions = () => {
  const { t } = useTranslation(['member', 'common']);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<MemberImportSession[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'accept' | 'reject';
    session: MemberImportSession | null;
  }>({ open: false, type: 'accept', session: null });
  const isMounted = useIsMounted();
  const signalR = useSignalRContext();

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getImportSessions();
      if (isMounted()) setSessions(result.data);
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  // Uppdatera listan automatiskt när import eller borttagning är klar
  signalR.useSignalREffect(`${SignalRMessageTypeEnum.MemberImportDone}`, () => fetchSessions(), [fetchSessions]);

  signalR.useSignalREffect(`${SignalRMessageTypeEnum.MemberImportRejectDone}`, () => fetchSessions(), [fetchSessions]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleAccept = async (session: MemberImportSession) => {
    setLoading(true);
    try {
      await acceptImportSession(session.importSession_id);
      if (isMounted()) {
        toast.success(t('member:importSessions.acceptSuccess') as string);
        fetchSessions();
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleReject = async (session: MemberImportSession) => {
    try {
      await rejectImportSession(session.importSession_id);
      if (isMounted()) {
        toast.info(t('member:importSessions.rejectInProgress') as string, { autoClose: 5000 });
        fetchSessions();
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    }
  };

  const openConfirm = (type: 'accept' | 'reject', session: MemberImportSession) => {
    setConfirmDialog({ open: true, type, session });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, type: 'accept', session: null });
  };

  const executeConfirm = () => {
    if (confirmDialog.session) {
      if (confirmDialog.type === 'accept') handleAccept(confirmDialog.session);
      else handleReject(confirmDialog.session);
    }
    closeConfirm();
  };

  const columns: GridColDef[] = [
    {
      field: 'fileName',
      headerName: t('member:importSessions.fileName'),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'imported',
      headerName: t('member:importSessions.imported'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.value === -1)
          return (
            <Chip label={t('member:importSessions.deleting')} size='small' color='error' icon={<CircularProgress size={14} color='inherit' />} />
          );
        if (params.value === 0)
          return (
            <Chip label={t('member:importSessions.inProgress')} size='small' color='warning' icon={<CircularProgress size={14} color='inherit' />} />
          );
        return params.value;
      },
    },
    {
      field: 'userName',
      headerName: t('member:importSessions.userName'),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'timeStamp',
      headerName: t('member:importSessions.timeStamp'),
      width: 160,
      valueFormatter: (params) => (params.value ? dayjs(params.value).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      field: 'actions',
      headerName: '',
      width: 250,
      sortable: false,
      renderCell: (params) => {
        const isImporting = params.row.imported === 0;
        const isDeleting = params.row.imported === -1;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size='small' variant='outlined' disabled={isImporting || isDeleting} onClick={() => openConfirm('accept', params.row)}>
              {t('member:importSessions.accept')}
            </Button>
            <Button size='small' variant='outlined' color='error' disabled={isImporting} onClick={() => openConfirm('reject', params.row)}>
              {t('member:importSessions.reject')}
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <FormLoaderWrapper loading={loading}>
      <Box>
        <Alert severity='info' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {t('member:importSessions.infoText')}
        </Alert>

        {sessions.length === 0 && !loading && (
          <Box sx={{ p: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              {t('member:importSessions.noSessions')}
            </Typography>
          </Box>
        )}

        {sessions.length > 0 && (
          <Box sx={{ width: '100%' }}>
            <DataGrid
              rows={sessions}
              columns={columns}
              getRowId={(row) => row.importSession_id}
              autoHeight
              disableRowSelectionOnClick
              hideFooter={sessions.length <= 50}
              initialState={{
                pagination: { paginationModel: { pageSize: 50, page: 0 } },
                sorting: { sortModel: [{ field: 'timeStamp', sort: 'desc' }] },
              }}
              pageSizeOptions={[50]}
            />
          </Box>
        )}

        {/* Bekräftelsedialog */}
        <Dialog open={confirmDialog.open} onClose={closeConfirm}>
          <DialogTitle>
            {confirmDialog.type === 'accept' ? t('member:importSessions.acceptConfirmTitle') : t('member:importSessions.rejectConfirmTitle')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmDialog.type === 'accept'
                ? t('member:importSessions.acceptConfirmText')
                : t('member:importSessions.rejectConfirmText', {
                    count: confirmDialog.session?.imported ?? 0,
                  })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={closeConfirm} color='primary'>
              {t('common:button.no')}
            </Button>
            <Button onClick={executeConfirm} color='primary' variant='contained'>
              {t('common:button.yes')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </FormLoaderWrapper>
  );
};
